import { RefObject, useEffect } from 'react';

type UseTouchDismissParams = {
  isActive: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  onDismiss: () => void;
};

const useTouchDismiss = ({ isActive, triggerRef, contentRef, onDismiss }: UseTouchDismissParams) => {
  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    let isOutsideTapStarted = false;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') {
        isOutsideTapStarted = false;

        return;
      }

      const target = event.target as Node;
      const isInside = triggerRef.current?.contains(target) || contentRef.current?.contains(target);

      isOutsideTapStarted = !isInside;
    };

    const handlePointerCancel = () => {
      isOutsideTapStarted = false;
    };

    const handlePointerUp = () => {
      if (isOutsideTapStarted) {
        isOutsideTapStarted = false;
        onDismiss();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('pointerup', handlePointerUp, true);
    document.addEventListener('pointercancel', handlePointerCancel, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      document.removeEventListener('pointercancel', handlePointerCancel, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, triggerRef, contentRef, onDismiss]);
};

export default useTouchDismiss;
